import { timeout } from 'widgets/toolbox/util';
import { debounce } from 'widgets/toolbox/debounce';

const KEY_TAB = 9;
/**
 * @typedef {typeof import('widgets/Widget').default} Widget
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 */

/**
 * @description Focus highlighter
 * @param {Widget} Widget Base widget for extending
 * @returns {typeof FocusHighlighter} Focus Highlighter class
 */
export default function (Widget) {
    /**
     * @category widgets
     * @subcategory global
     * @class FocusHighlighter
     * @augments Widget
     * @classdesc This class intended to address Accessibility issue with focus highlighting on links element.
     * Usually used on `<body>` tag to observe focus move events
     *
     * In case if page was focused, next subsequent `TAB` press will leads to next available link/input element focus highlighting
     * Shows bordered rectangle, which fits currently focused element and moves rectangle to element's position.
     * @property {string} data-widget - Widget name `focusHighlighter`
     * @property {string} data-event-keyup - Event handler for `handleKeyup` method
     * @property {string} data-event-click - Event handler for `handleClick` method
     * @property {string} data-classes-inited - Inited classes, added to element after initialisation
     * @property {string} data-classes-hurry - Classes, which don't contains animation and used to move `highlighter` (bordered rectangle) between focused elements without animation.
     * @example <caption>FocusHighlighter widget</caption>
     * <body
     *   class="b-page"
     *   data-widget="focusHighlighter"
     *   data-event-keyup="handleKeyup"
     *   data-event-click="handleClick"
     * >
     *     ... page content here
     * </body>
     */
    class FocusHighlighter extends Widget {
        prefs() {
            return {
                classesInited: 'b-highlighter_inited',
                classesEnabled: 'm-visible',
                classesHurry: 'm-hurry',
                DELAY_TIMEOUT: 200, // Delay for animation

                ...super.prefs()
            };
        }

        /**
         * @description Widget initialisation logic
         * @returns {void}
         */
        init() {
            this.keyboardModality = false;
            this.isHighlighterVisible = false;
            this.lastFocusedElement = null;
            this.lastFocusedElementCoords = '';
            this.highlighter = null;

            this.ref('self').addClass(this.prefs().classesInited);
            this.ev('resize', debounce(this.handleResize.bind(this), 200), window);
            this.eventBus().on('rendering.applied', 'handleResize');
        }

        /**
         * @param {refElement} ref - key up event referenced element
         * @param {KeyboardEvent} event - key up event
         * @listens dom#keyup
         * @returns {void}
         */
        handleKeyup(ref, event) {
            if (event.keyCode === KEY_TAB) {
                this.enableHighlighter();
            }
        }

        /**
         * @param {refElement} ref - click event referenced element
         * @param {MouseEvent} event - click event
         * @listens dom#click
         * @returns {void}
         */
        handleClick(ref, event) {
            // Space keyup on button DomNode generates synthetic click
            // so we need to ensure that it is not the one
            if (this.isHighlighterVisible && !(event.x === 0 && event.y === 0)) {
                this.disableHighlighter();
            }
        }

        /**
         * @description Window resize event handler
         * @listens dom#resize
         * @returns {void}
         */
        handleResize() {
            if (this.isHighlighterVisible) {
                this.moveTo(this.lastFocusedElement);
            }
        }

        /**
         * @description Handles focus change on page
         * @listens dom#focusin
         * @returns {void}
         */
        handleFocus() {
            if (!(document.activeElement instanceof HTMLElement)) { return; }

            const focusedElement = document.activeElement;

            if (
                !this.isValidTarget(focusedElement)
                || (this.isTextInput(focusedElement) && !this.keyboardModality)
            ) {
                return;
            }

            this.detectHurryNavigation();
            this.moveTo(focusedElement);

            // We need to recheck focused element position since coords could
            // be changed during scroll in carousels, animation, dynamic page changes
            if (this.rechekTimeout) { this.rechekTimeout(); }
            if (this.rechekSecondTimeout) { this.rechekSecondTimeout(); }

            this.rechekTimeout = timeout(() => this.moveTo(focusedElement), 400);
            this.rechekSecondTimeout = timeout(() => this.moveTo(focusedElement), 800);
        }

        /**
         * @description Enables a `highlighter` - a bordered box with sizes of currently focused element.
         * @returns {void}
         */
        enableHighlighter() {
            if (this.keyboardModality) {
                return;
            }
            this.keyboardModality = true;

            this.ref('highlighter').addClass(this.prefs().classesEnabled);
            this.isHighlighterVisible = true;
            this.onDestroy(() => this.ref('highlighter').removeClass(this.prefs().classesEnabled));

            this.handleFocus();

            this.focusinHandlers = this.ev('focusin', this.handleFocus, document);
            // all other events are handled by focusin event
        }

        /**
         * @description Disables a `highlighter` - a bordered box with sizes of currently focused element.
         * @returns {void}
         */
        disableHighlighter() {
            if (!this.keyboardModality) {
                return;
            }
            this.keyboardModality = false;

            this.ref('highlighter').removeClass(this.prefs().classesEnabled);
            this.isHighlighterVisible = false;
            this.hideTimeOut = timeout(this.hide.bind(this), this.prefs().DELAY_TIMEOUT);

            if (this.focusinHandlers) {
                this.focusinHandlers.forEach(fn => fn());
            }
            this.lastFocusedElement = null;
        }

        /**
         * @description Moves `highlighter` (a bordere box) in place of focused element
         * @param {HTMLElement} focusedElement - element, which gets focus
         */
        moveTo(focusedElement) {
            if (!(focusedElement instanceof HTMLElement)) { return; }

            const highlighterNode = this.ref('highlighter').get();
            if (!highlighterNode) { return; }

            const targetRect = focusedElement.getBoundingClientRect();
            const targetTop = targetRect.top + window.scrollY;
            const targetLeft = targetRect.left + window.scrollX;
            const targetWidth = focusedElement.offsetWidth;
            const targetHeight = focusedElement.offsetHeight;

            if (
                focusedElement === this.lastFocusedElement
                && this.lastFocusedElementCoords === '' + targetTop + targetLeft + targetWidth + targetHeight
            ) {
                // If we come from coords recheck do not reapply changes
                return;
            }

            const highlighterStyle = highlighterNode.style;
            highlighterStyle.top = `${targetTop - 5}px`;
            highlighterStyle.left = `${targetLeft - 5}px`;
            highlighterStyle.width = `${targetWidth + 2}px`;
            highlighterStyle.height = `${targetHeight + 2}px`;

            this.lastFocusedElementCoords = '' + targetTop + targetLeft + targetWidth + targetHeight;
            this.lastFocusedElement = focusedElement;
        }

        /**
         * @description Hide `highlighter`
         * @returns {this} result
         */
        hide() {
            const highlighterNode = this.ref('highlighter').get();
            if (!highlighterNode) {
                return this;
            }
            const highlighterStyle = highlighterNode.style;

            highlighterStyle.width = '0';
            highlighterStyle.height = '0';

            if (this.hideTimeOut) {
                this.hideTimeOut();
            }

            return this;
        }

        /**
         * @description Detects too fast customer navigation, and displays `highlighter`,
         * moving from previously focused element to currently focused without animation.
         * @returns {void}
         */
        detectHurryNavigation() {
            const currentTime = Date.now();

            const isHurryNavigation = (currentTime - (this.lastKeyTime || 0)) < 190;

            this.ref('highlighter').toggleClass(this.prefs().classesHurry, isHurryNavigation);
            this.isHurryNavigation = isHurryNavigation;

            this.lastKeyTime = currentTime;
        }

        /**
         * @param {HTMLElement} domNode - focused element
         * @returns {boolean} is HTML element valid target for focus
         */
        isValidTarget(domNode) {
            return domNode !== this.lastFocusedElement
                && domNode.nodeName !== 'HTML'
                && domNode.nodeName !== 'BODY';
        }

        /**
         * @param {HTMLTextAreaElement|HTMLInputElement|HTMLElement} domNode - focused element
         * @returns {boolean} is HTML element text input
         */
        isTextInput(domNode) {
            // @ts-ignore
            return (domNode.tagName === 'TEXTAREA' && !domNode.readOnly)
                // @ts-ignore
                || (domNode.tagName === 'INPUT' && !domNode.readOnly)
                || !!(domNode.getAttribute('contenteditable'));
        }
    }

    return FocusHighlighter;
}
